/**
 * The one path through the wall, as addresses: the wall (`@core/wall/pageVerb` `wallScopePath`) hands its own address to
 * the intake page, the intake hands it on to the feature page, and every way out of the path comes back by it. `back`
 * is that wall address; nothing else is carried.
 */
import {readWallScope, wallScopePath} from '@core/wall/pageVerb'

const withBack = (path: string, back: string | null) => (back ? `${path}?back=${encodeURIComponent(back)}` : path)

export const intakePath = (back: string | null) => withBack('/model', back)
export const proofPath = (back: string | null) => withBack('/proof', back)

/** The wall address a path page was opened with, or null: a root-relative path on this site, nothing else. */
export function backOf(search: URLSearchParams): string | null {
    const raw = search.get('back')
    return raw !== null && /^\/(?![/\\])/.test(raw) && raw.length <= 1024 ? raw : null
}

/** A route's `handle` that takes the site footer off its page for the given address; the layout reads it. */
export type FooterHandle = {footerless?: (search: URLSearchParams) => boolean}

const searchOf = (back: string) => new URL(back, 'https://narcis.invalid').searchParams

/** The pack whose nine the visitor asked for on the wall, or null: the `back` address names it with `intent=pack`. */
export function packIntentOf(back: string | null): string | null {
    if (!back) return null
    const {pack, intent} = readWallScope(searchOf(back))
    return intent ? pack : null
}

/** The same wall, once the nine are asked for: the intent is spent, the search and the armed tile stay. */
export function wallAfterQueue(back: string | null): string {
    if (!back) return '/'
    const search = searchOf(back)
    const basis = search.get('basis')
    return wallScopePath({
        q: search.get('q') ?? '',
        tileId: basis !== null && /^\d+$/.test(basis) ? Number(basis) : null,
        pack: readWallScope(search).pack,
        intent: false,
    })
}

/** The feature page's address carries this when the visitor comes back from the sign-in it asked for at the pay button. */
export const SIGNED_PARAM = 'signed'

/** The tile whose single photo the visitor asked for, or null: the `back` address arms it and names no pack intent. */
export function tileIntentOf(back: string | null): number | null {
    if (!back || packIntentOf(back)) return null
    const basis = searchOf(back).get('basis')
    return basis !== null && /^\d+$/.test(basis) ? Number(basis) : null
}

/** The wall address carries this once, on the landing after a payment: the wall says it once and takes it off. */
export const PAID_PARAM = 'paid'

/** `wallAfterQueue`, marked as the landing after a payment. */
export function wallAfterPayment(back: string | null): string {
    const wall = wallAfterQueue(back)
    return `${wall}${wall.includes('?') ? '&' : '?'}${PAID_PARAM}=1`
}

/** The intake's way in: the wall path, a retrain, or the link that carried `?from=` (the closed list below). */
const INTAKE_ORIGINS = ['header', 'pack', 'pricing', 'issue'] as const
export type IntakeFrom = 'wall' | 'retrain' | (typeof INTAKE_ORIGINS)[number] | 'direct'

/** A plain link to the intake, marked with where it sits. */
export const intakeFromLink = (from: (typeof INTAKE_ORIGINS)[number]) => `/model?from=${from}`

export function intakeFrom(search: URLSearchParams, hasModel: boolean): IntakeFrom {
    if (backOf(search)) return 'wall'
    if (hasModel) return 'retrain'
    const from = search.get('from')
    return INTAKE_ORIGINS.find((origin) => origin === from) ?? 'direct'
}
